/**
 * Selector engine for the tutorial recorder.
 *
 * Turns a clicked DOM node into the most durable CSS selector we can find, so a
 * recorded session still resolves after unrelated markup changes. Anything that
 * falls back to a positional path is flagged as unstable, which is the signal
 * that the element needs a hand-authored `data-tutorial` anchor.
 */

// Attributes are tried in this order; the earlier ones survive refactors best.
const ANCHOR_ATTRIBUTES = [
  'data-tutorial',
  'data-testid',
  'data-test',
  'data-id',
  'data-action',
  'data-sample',
];

const INTERACTIVE_TAGS = new Set([
  'BUTTON',
  'A',
  'INPUT',
  'SELECT',
  'TEXTAREA',
  'LABEL',
  'SUMMARY',
  'ION-BUTTON',
  'ION-ITEM',
  'ION-SEGMENT-BUTTON',
  'ION-CHECKBOX',
  'ION-TOGGLE',
  'ION-SELECT',
  'ION-RANGE',
  'ION-TAB-BUTTON',
]);

// Framework-generated or transient classes that change between renders.
const UNSTABLE_CLASS_PATTERNS = [
  /^css-/,
  /^sc-/,
  /^emotion-/,
  /^jsx-/,
  /^_/,
  /^ng-/,
  /^ion-(activated|focused|color)/,
  /^(md|ios|hydrated)$/,
  /^(active|selected|open|closed|hover|hovered|focus|focused|disabled|loading|visible|hidden|expanded|collapsed|dragging|is-[a-z-]+|has-[a-z-]+)$/,
  // Interaction state suffixes, e.g. Ant's `ant-tooltip-open`, which lands on
  // whichever control the pointer is over and would otherwise look unique.
  /-(open|active|focused|selected|checked|disabled|loading|hover|dragging|visible|hidden)$/,
  /\d{4,}/,
  /^[a-f0-9]{8,}$/i,
];

const isUnstableClass = (token) =>
  !token || UNSTABLE_CLASS_PATTERNS.some((pattern) => pattern.test(token));

const isUnstableId = (id) =>
  !id ||
  /\d{4,}/.test(id) ||
  /^[a-f0-9]{8,}$/i.test(id) ||
  /^(radix|headless|mui|react-aria|:r)/i.test(id);

export const cssEscape = (value) =>
  window.CSS && window.CSS.escape ? window.CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');

const isUnique = (selector) => {
  try {
    return document.querySelectorAll(selector).length === 1;
  } catch (e) {
    return false;
  }
};

const stableClasses = (el) =>
  Array.from(el.classList || []).filter((token) => !isUnstableClass(token));

/**
 * Ionic renders into shadow roots, so the deepest event target is often
 * unreachable from `document.querySelector`. Hop up to the light-DOM host.
 */
export const toLightDomElement = (node) => {
  let el = node;
  while (el && !document.contains(el)) {
    const root = el.getRootNode();
    if (root && root.host) {
      el = root.host;
    } else {
      return null;
    }
  }
  return el && el.nodeType === 1 ? el : null;
};

/**
 * Walk the composed path outward to the element a user would say they clicked
 * (the button, not the icon inside it).
 */
export const resolveInteractiveTarget = (event) => {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];

  for (const node of path) {
    if (!node || node.nodeType !== 1) continue;
    const el = toLightDomElement(node);
    if (!el) continue;

    const hasAnchor = ANCHOR_ATTRIBUTES.some((attr) => el.hasAttribute(attr));
    const isInteractive =
      INTERACTIVE_TAGS.has(el.tagName) ||
      el.getAttribute('role') === 'button' ||
      el.getAttribute('role') === 'tab' ||
      el.hasAttribute('onclick') ||
      el.tabIndex >= 0;

    if (hasAnchor || isInteractive) return el;
  }

  return toLightDomElement(path[0] || event.target);
};

const positionalPath = (el, stopAt) => {
  const segments = [];
  let current = el;

  while (current && current.nodeType === 1 && current !== document.body && current !== stopAt && segments.length < 6) {
    const node = current;
    const tag = node.tagName.toLowerCase();
    const parent = node.parentElement;

    if (!parent) {
      segments.unshift(tag);
      break;
    }

    const siblings = Array.from(parent.children).filter((c) => c.tagName === node.tagName);
    const classes = stableClasses(node).slice(0, 2);
    let segment = tag + classes.map((c) => `.${cssEscape(c)}`).join('');

    if (siblings.length > 1) {
      segment += `:nth-of-type(${siblings.indexOf(node) + 1})`;
    }

    segments.unshift(segment);
    current = parent;
  }

  return segments.join(' > ');
};

/**
 * Returns every selector we considered plus the winner, so a recording can be
 * audited later without replaying it.
 */
export const buildSelector = (el) => {
  const candidates = [];

  if (!el) {
    return { selector: null, stable: false, candidates, matchedOn: 'none' };
  }

  // The element's own anchor always wins. Anything inherited from an ancestor
  // is a fallback, because a container anchor (a screen root, say) would
  // otherwise swallow every control inside it.
  for (const attr of ANCHOR_ATTRIBUTES) {
    if (!el.hasAttribute(attr)) continue;

    const selector = `[${attr}="${cssEscape(el.getAttribute(attr))}"]`;
    candidates.push(selector);

    if (isUnique(selector)) {
      return { selector, stable: true, candidates, matchedOn: attr, exact: true };
    }
  }

  if (el.id && !isUnstableId(el.id)) {
    const selector = `#${cssEscape(el.id)}`;
    candidates.push(selector);
    if (isUnique(selector)) {
      return { selector, stable: true, candidates, matchedOn: 'id', exact: true };
    }
  }

  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel) {
    const selector = `${el.tagName.toLowerCase()}[aria-label="${cssEscape(ariaLabel)}"]`;
    candidates.push(selector);
    if (isUnique(selector)) {
      return { selector, stable: true, candidates, matchedOn: 'aria-label', exact: true };
    }
  }

  const name = el.getAttribute('name');
  if (name) {
    const selector = `${el.tagName.toLowerCase()}[name="${cssEscape(name)}"]`;
    candidates.push(selector);
    if (isUnique(selector)) {
      return { selector, stable: true, candidates, matchedOn: 'name', exact: true };
    }
  }

  const classes = stableClasses(el);
  if (classes.length) {
    const selector = el.tagName.toLowerCase() + classes.map((c) => `.${cssEscape(c)}`).join('');
    candidates.push(selector);
    if (isUnique(selector)) {
      return { selector, stable: true, candidates, matchedOn: 'class', exact: true };
    }
  }

  // Nothing identifies the element on its own. Scope a short positional path to
  // the nearest anchored ancestor — still brittle, but it survives changes
  // outside that container, and it points at the element rather than the box
  // around it.
  for (const attr of ANCHOR_ATTRIBUTES) {
    const owner = el.closest(`[${attr}]`);
    if (!owner || owner === el) continue;

    const scope = `[${attr}="${cssEscape(owner.getAttribute(attr))}"]`;
    const relative = positionalPath(el, owner);
    if (!relative) continue;

    const selector = `${scope} ${relative}`;
    candidates.push(selector);

    if (isUnique(selector)) {
      return { selector, stable: false, candidates, matchedOn: `${attr} (scoped path)`, exact: true };
    }
  }

  const path = positionalPath(el);
  candidates.push(path);

  return {
    selector: path,
    stable: false,
    candidates,
    matchedOn: 'dom-path',
    exact: true,
  };
};

const FORM_CONTROL_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);

/**
 * A form control's own value is a poor label — it changes as the user types and
 * produces anchor names like "1-3". Look for the surrounding wording instead.
 */
const readControlLabel = (el) => {
  const wrapping = el.closest('label');
  if (wrapping) {
    const text = (wrapping.innerText || '').replace(/\s+/g, ' ').trim();
    if (text) return text;
  }

  if (el.id) {
    try {
      const linked = document.querySelector(`label[for="${cssEscape(el.id)}"]`);
      const text = linked && (linked.innerText || '').replace(/\s+/g, ' ').trim();
      if (text) return text;
    } catch (e) {
      /* malformed id */
    }
  }

  const previous = el.previousElementSibling;
  const previousText = previous && (previous.innerText || '').replace(/\s+/g, ' ').trim();
  if (previousText) return previousText;

  const parentText = el.parentElement && (el.parentElement.innerText || '').replace(/\s+/g, ' ').trim();
  if (parentText) return parentText;

  return '';
};

export const readLabel = (el) => {
  if (!el) return '';

  const direct =
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    el.getAttribute('placeholder') ||
    el.getAttribute('alt');
  if (direct) return direct.trim();

  if (FORM_CONTROL_TAGS.has(el.tagName)) {
    const contextual = readControlLabel(el);
    if (contextual) return contextual.slice(0, 80);
    return el.getAttribute('name') || el.type || 'field';
  }

  const text = (el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim();
  if (text) return text.slice(0, 80);

  return '';
};

/** Kebab-cased anchor name to suggest for elements that lack one. */
export const suggestAnchorName = (el, label) => {
  const base = (label || (el && el.tagName) || 'target')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .split('-')
    .slice(0, 4)
    .join('-');

  return base || 'target';
};

/**
 * Coarse "which screen am I on" marker so recorded steps can be grouped into
 * chapters without replaying the session.
 */
const SCREEN_MARKERS = [
  ['stitch-editor', '[data-tutorial="stitch-editor"], [data-stitch-editor]'],
  ['svg-editor', '[data-tutorial="svg-canvas"]'],
  ['converter', '[data-tutorial="original-image-only"], [data-tutorial="color-layers"]'],
  ['home', '[data-tutorial="upload-area"], [data-tutorial="upload-button"]'],
];

export const detectScreen = () => {
  for (const [name, selector] of SCREEN_MARKERS) {
    try {
      if (document.querySelector(selector)) return name;
    } catch (e) {
      /* ignore malformed selector */
    }
  }
  return 'unknown';
};

const DESIGN_SVG = '[data-pan-zoom="design"]';
const CANVAS = '[data-tutorial="stitch-canvas"]';

/**
 * Where a canvas click landed in the design's own coordinates.
 *
 * A position on the canvas only means anything at the zoom, pan and window size
 * it was demonstrated at — replay it on another screen and it misses the shape.
 * The design's SVG carries the transform from its coordinates to the screen, so
 * running a click back through the inverse records the point on the artwork
 * itself, which is what a tutorial step can be written against.
 */
const readDesignPoint = (el, event) => {
  if (!el || typeof el.closest !== 'function' || !el.closest(CANVAS)) return null;
  if (!event || typeof event.clientX !== 'number') return null;

  const svg = document.querySelector(DESIGN_SVG);
  if (!svg || typeof svg.createSVGPoint !== 'function') return null;

  const matrix = svg.getScreenCTM();
  if (!matrix) return null;

  const point = svg.createSVGPoint();
  point.x = event.clientX;
  point.y = event.clientY;

  const { x, y } = point.matrixTransform(matrix.inverse());
  return { x: Math.round(x), y: Math.round(y) };
};

export const describeElement = (el, event) => {
  if (!el) return null;

  const label = readLabel(el);
  const rect = el.getBoundingClientRect();
  const { selector, stable, candidates, matchedOn } = buildSelector(el);

  // Where inside the element the pointer landed. Canvas gestures all share one
  // huge target, so the position is the only thing distinguishing them.
  let offsetPct = null;
  if (event && rect.width > 0 && rect.height > 0 && typeof event.clientX === 'number') {
    offsetPct = {
      x: Math.round(((event.clientX - rect.left) / rect.width) * 1000) / 10,
      y: Math.round(((event.clientY - rect.top) / rect.height) * 1000) / 10,
    };
  }

  return {
    offsetPct,
    designPoint: readDesignPoint(el, event),
    selector,
    stable,
    candidates,
    matchedOn,
    anchorSuggestion: stable ? null : suggestAnchorName(el, label),
    label,
    tag: el.tagName.toLowerCase(),
    role: el.getAttribute('role') || null,
    id: el.id || null,
    classes: Array.from(el.classList || []).join(' ') || null,
    domPath: positionalPath(el),
    rect: {
      top: Math.round(rect.top),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    },
  };
};
